(() => {
  const experienceSelector = 'sesami-experience';
  const interfaceSelector = 'sesami-navigation, sesami-monthly-calendar';
  const interfaceContainerSelector = 'sesami-window';
  const buttonStyleAttribute = 'data-theme-button-typography';
  const interfaceStyleAttribute = 'data-theme-interface-typography';
  const lightDomStyleAttribute = 'data-theme-sesami-typography';
  const observedRoots = new WeakSet();
  const pendingComponents = new WeakSet();
  const bookingRequests = new Map();
  const observedBookingFields = new WeakSet();

  const getThemeValue = (property, fallback) => {
    const value = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(property)
      .trim();

    return value || fallback;
  };

  const getThemeTypography = () => ({
    bodyFamily: getThemeValue('--font-body-family', window.getComputedStyle(document.body).fontFamily),
    bodyStyle: getThemeValue('--font-body-style', 'normal'),
    bodyWeight: getThemeValue('--font-body-weight', '400'),
    bodyLineHeight: getThemeValue('--font-body-line-height', '1.4'),
    headingFamily: getThemeValue('--font-heading-family', window.getComputedStyle(document.body).fontFamily),
    headingStyle: getThemeValue('--font-heading-style', 'normal'),
    headingWeight: getThemeValue('--font-heading-weight', '600'),
    headingLetterSpacing: getThemeValue('--font-heading-letter-spacing', 'normal'),
    headingTextTransform: getThemeValue('--font-heading-text-transform', 'none'),
    buttonFamily: getThemeValue('--font-button-family', window.getComputedStyle(document.body).fontFamily),
    buttonStyle: getThemeValue('--font-button-style', 'normal'),
    buttonWeight: getThemeValue('--font-button-weight', '600'),
    buttonLetterSpacing: getThemeValue('--font-button-letter-spacing', 'normal'),
    buttonTextTransform: getThemeValue('--font-button-text-transform', 'none'),
    buttonSize: getThemeValue('--font-size-button', '1.4rem'),
  });

  const getBookingButtonLabel = (experience) => {
    const productSection = experience.closest('[data-sesami-booking-label]');
    return productSection?.dataset.sesamiBookingLabel?.trim() || '';
  };

  const applyBookingButtonLabel = (experience, shadowRoot) => {
    const label = getBookingButtonLabel(experience);
    if (!label) return;

    if (experience.getAttribute('button-label') !== label) {
      experience.setAttribute('button-label', label);
    }

    const button = shadowRoot.querySelector('.sesami-calendar');
    if (!button || button.textContent.trim() === label) return;

    const labelNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );

    if (labelNode) {
      labelNode.textContent = label;
    } else {
      button.appendChild(document.createTextNode(label));
    }
  };

  const getSesamiEventContext = (event) => {
    const tagId = event?.detail?.tagId ?? event?.detail?.tag_id;
    const experiences = Array.from(document.querySelectorAll(experienceSelector));
    const experience =
      experiences.find((item) => String(item.getAttribute('tag-id')) === String(tagId)) ||
      (experiences.length === 1 ? experiences[0] : null);
    const productSection = experience?.closest('[id^="MainProduct-"]');
    const form = productSection?.querySelector('[data-sesami-product-form]');

    return {
      tagId: tagId ?? experience?.getAttribute('tag-id'),
      experience,
      productSection,
      form
    };
  };

  const getSesamiFields = ({ tagId }) => {
    if (tagId) {
      const fieldsWrapper = document.getElementById(`sesami-hidden-fields-${tagId}`);
      if (fieldsWrapper) return fieldsWrapper;
    }

    const fieldWrappers = document.querySelectorAll('[id^="sesami-hidden-fields-"]');
    return fieldWrappers.length === 1 ? fieldWrappers[0] : null;
  };

  const getReservationFields = (context) => {
    const fieldsWrapper = getSesamiFields(context);
    if (!fieldsWrapper) return {};

    const dateField =
      fieldsWrapper.querySelector('[sesami-hidden-date]') ||
      fieldsWrapper.querySelector('input[name*="[Date]"]');
    const timeFields = Array.from(
      fieldsWrapper.querySelectorAll('[sesami-hidden-time], input[name*="[Time]"], input[name*="[Heure]"]')
    );
    const timeField = timeFields.find((field) => !/timezone|fuseau/i.test(field.name)) || timeFields[0];

    return { fieldsWrapper, dateField, timeField };
  };

  const sesamiFieldsAreReady = (context) => {
    const { dateField, timeField } = getReservationFields(context);

    return Boolean(dateField?.value.trim() && timeField?.value.trim());
  };

  const appendSesamiFields = (formData, context) => {
    const fieldsWrapper = getSesamiFields(context);
    fieldsWrapper?.querySelectorAll('input[name], select[name], textarea[name]').forEach((field) => {
      if (field.name) formData.set(field.name, field.value);
    });
  };

  const clearSesamiFields = (context) => {
    const fieldsWrapper = getSesamiFields(context);
    fieldsWrapper?.querySelectorAll('input, select, textarea').forEach((field) => {
      field.value = '';
    });
  };

  const setBookingLoading = (context, loading) => {
    context.experience?.toggleAttribute('data-booking-loading', loading);
    window.dispatchEvent(
      new CustomEvent('theme:sesami-booking-loading', {
        detail: { tagId: context.tagId, loading }
      })
    );
  };

  const addBookingToCart = async (context) => {
    const { form } = context;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData(form);
    appendSesamiFields(formData, context);

    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add the Sesami booking to the cart.');
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'sesami-experience',
        productVariantId: formData.get('id')
      });
    }

    cart?.renderContents(result);
    clearSesamiFields(context);
  };

  const addBookingWhenReady = (context) => {
    if (!context.form || !context.productSection?.hasAttribute('data-sesami-service-ateliers')) return;

    const requestKey = String(context.tagId || context.form.id);
    if (bookingRequests.has(requestKey)) return;

    bookingRequests.set(requestKey, true);
    setBookingLoading(context, true);
    let attemptCount = 0;

    const waitForFields = async () => {
      attemptCount += 1;

      if (!sesamiFieldsAreReady(context)) {
        if (attemptCount < 100) {
          window.setTimeout(waitForFields, 50);
        } else {
          bookingRequests.delete(requestKey);
          setBookingLoading(context, false);
          console.error('Sesami: date or time is missing.');
        }
        return;
      }

      try {
        await addBookingToCart(context);
      } catch (error) {
        console.error('Sesami: unable to add the booking to the cart.', error);
      } finally {
        window.setTimeout(() => bookingRequests.delete(requestKey), 1000);
        setBookingLoading(context, false);
      }
    };

    waitForFields();
  };

  const observeBookingFields = (context) => {
    let attemptCount = 0;

    const findAndObserveFields = () => {
      attemptCount += 1;
      const fieldsWrapper = getSesamiFields(context);

      if (!fieldsWrapper) {
        if (attemptCount < 100) window.setTimeout(findAndObserveFields, 50);
        return;
      }

      const triggerAddToCart = () => {
        if (sesamiFieldsAreReady(context)) addBookingWhenReady(context);
      };

      if (!observedBookingFields.has(fieldsWrapper)) {
        fieldsWrapper.addEventListener('change', triggerAddToCart);
        fieldsWrapper.addEventListener('input', triggerAddToCart);
        observedBookingFields.add(fieldsWrapper);
      }
    };

    findAndObserveFields();
  };

  window.addEventListener('sesami:modal:opened', (event) => {
    observeBookingFields(getSesamiEventContext(event));
  });

  window.addEventListener('sesami:reservation:success', (event) => {
    addBookingWhenReady(getSesamiEventContext(event));
  });

  window.addEventListener('sesami:booking:success', (event) => {
    addBookingWhenReady(getSesamiEventContext(event));
  });

  const injectLightDomTypography = () => {
    const typography = getThemeTypography();
    const lightDomCss = `
      sesami-experience .sesami-calendar {
        font-family: ${typography.buttonFamily} !important;
        font-size: ${typography.buttonSize} !important;
        font-style: ${typography.buttonStyle} !important;
        font-weight: ${typography.buttonWeight} !important;
        letter-spacing: ${typography.buttonLetterSpacing} !important;
        text-transform: ${typography.buttonTextTransform} !important;
      }

      sesami-navigation,
      sesami-navigation *,
      sesami-monthly-calendar,
      sesami-monthly-calendar * {
        font-family: ${typography.bodyFamily} !important;
      }

      sesami-navigation h1,
      sesami-navigation h2,
      sesami-navigation h3,
      sesami-navigation h4,
      sesami-navigation h5,
      sesami-navigation h6,
      sesami-navigation [role='heading'],
      sesami-monthly-calendar h1,
      sesami-monthly-calendar h2,
      sesami-monthly-calendar h3,
      sesami-monthly-calendar h4,
      sesami-monthly-calendar h5,
      sesami-monthly-calendar h6,
      sesami-monthly-calendar [role='heading'] {
        font-family: ${typography.headingFamily} !important;
      }

      sesami-navigation button,
      sesami-navigation [role='button'],
      sesami-navigation input[type='button'],
      sesami-navigation input[type='submit'],
      sesami-monthly-calendar button,
      sesami-monthly-calendar [role='button'],
      sesami-monthly-calendar input[type='button'],
      sesami-monthly-calendar input[type='submit'] {
        font-family: ${typography.buttonFamily} !important;
      }
    `;

    let style = document.head.querySelector(`[${lightDomStyleAttribute}]`);
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(lightDomStyleAttribute, '');
      document.head.appendChild(style);
    }
    if (style.textContent !== lightDomCss) style.textContent = lightDomCss;
  };

  const observeRoot = (root, reinjectStyles) => {
    if (!root || observedRoots.has(root)) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initializeComponents(node);
        });
      });

      reinjectStyles?.();
    });

    observer.observe(root, { childList: true, characterData: true, subtree: true });
    observedRoots.add(root);
  };

  const injectButtonTypography = (experience) => {
    const shadowRoot = experience.shadowRoot;

    if (!shadowRoot) return false;

    const typography = getThemeTypography();
    const buttonCss = `
      .sesami-calendar {
        font-family: ${typography.buttonFamily} !important;
        font-size: ${typography.buttonSize} !important;
        font-style: ${typography.buttonStyle} !important;
        font-weight: ${typography.buttonWeight} !important;
        line-height: ${typography.bodyLineHeight} !important;
        letter-spacing: ${typography.buttonLetterSpacing} !important;
        text-transform: ${typography.buttonTextTransform} !important;
        margin: 1.6rem 0 !important;
      }

    `;

    let style = shadowRoot.querySelector(`[${buttonStyleAttribute}]`);

    if (!style) {
      style = document.createElement('style');
      style.setAttribute(buttonStyleAttribute, '');
      shadowRoot.appendChild(style);
    }

    if (style.textContent !== buttonCss) style.textContent = buttonCss;
    if (style !== shadowRoot.lastElementChild) shadowRoot.appendChild(style);
    applyBookingButtonLabel(experience, shadowRoot);
    experience.dataset.themeTypographyReady = 'true';

    initializeComponents(shadowRoot);
    observeRoot(shadowRoot, () => injectButtonTypography(experience));

    return true;
  };

  const injectInterfaceTypography = (component) => {
    const shadowRoot = component.shadowRoot;

    if (!shadowRoot) return false;

    const typography = getThemeTypography();
    const interfaceCss = `
      :host,
      :host * {
        font-family: ${typography.bodyFamily} !important;
      }

      :host h1,
      :host h2,
      :host h3,
      :host h4,
      :host h5,
      :host h6,
      :host [role='heading'] {
        font-family: ${typography.headingFamily} !important;
        font-style: ${typography.headingStyle} !important;
        font-weight: ${typography.headingWeight} !important;
        letter-spacing: ${typography.headingLetterSpacing} !important;
        text-transform: ${typography.headingTextTransform} !important;
      }

      :host button,
      :host [role='button'],
      :host input[type='button'],
      :host input[type='submit'] {
        font-family: ${typography.buttonFamily} !important;
        font-style: ${typography.buttonStyle} !important;
        font-weight: ${typography.buttonWeight} !important;
        letter-spacing: ${typography.buttonLetterSpacing} !important;
        text-transform: ${typography.buttonTextTransform} !important;
      }

      :host input,
      :host select,
      :host textarea {
        font-family: ${typography.bodyFamily} !important;
        font-style: ${typography.bodyStyle} !important;
        font-weight: ${typography.bodyWeight} !important;
      }
    `;

    let style = shadowRoot.querySelector(`[${interfaceStyleAttribute}]`);

    if (!style) {
      style = document.createElement('style');
      style.setAttribute(interfaceStyleAttribute, '');
      shadowRoot.appendChild(style);
    }

    if (style.textContent !== interfaceCss) style.textContent = interfaceCss;
    if (style !== shadowRoot.lastElementChild) shadowRoot.appendChild(style);
    component.dataset.themeTypographyReady = 'true';

    initializeComponents(shadowRoot);
    observeRoot(shadowRoot, () => injectInterfaceTypography(component));

    return true;
  };

  const initializeInterfaceContainer = (container) => {
    const shadowRoot = container.shadowRoot;

    if (!shadowRoot) return false;

    initializeComponents(shadowRoot);
    observeRoot(shadowRoot);

    return true;
  };

  const initializeComponent = (component, injectStyles) => {
    if (injectStyles(component) || pendingComponents.has(component)) return;

    pendingComponents.add(component);
    let attemptCount = 0;
    const waitForShadowRoot = window.setInterval(() => {
      attemptCount += 1;

      if (injectStyles(component) || attemptCount >= 100) {
        window.clearInterval(waitForShadowRoot);
        pendingComponents.delete(component);
      }
    }, 100);
  };

  const initializeComponents = (root = document) => {
    if (root.matches?.(experienceSelector)) {
      initializeComponent(root, injectButtonTypography);
    }
    if (root.matches?.(interfaceSelector)) {
      initializeComponent(root, injectInterfaceTypography);
    }
    if (root.matches?.(interfaceContainerSelector)) {
      initializeComponent(root, initializeInterfaceContainer);
    }

    root.querySelectorAll?.(experienceSelector).forEach((experience) => {
      initializeComponent(experience, injectButtonTypography);
    });
    root.querySelectorAll?.(interfaceSelector).forEach((component) => {
      initializeComponent(component, injectInterfaceTypography);
    });
    root.querySelectorAll?.(interfaceContainerSelector).forEach((container) => {
      initializeComponent(container, initializeInterfaceContainer);
    });
  };

  injectLightDomTypography();
  initializeComponents();
  observeRoot(document.documentElement);
  document.addEventListener('shopify:section:load', (event) => {
    injectLightDomTypography();
    initializeComponents(event.target);
  });
})();
